import { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  getAvailableChangePlans,
  getAvailableSignUpPlans,
  getPlanFromList,
  getPlansWithConfigStrings,
} from 'Utils/Plans';
import PLAN_STRINGS_CONFIG from 'Config/PlanStrings.json';
import { getPlans, selectPlan, clearSelectedPlan } from 'State/Plans/PlansActions';
import { getSetDefaultPlanIndex, isDefaultPlanFlow } from 'Utils/Analytics/Amplitude';
import { setPurchaseMode } from 'State/PurchaseMode/PurchaseModeActions';
import { useSubscription } from 'Hooks/useSubscription';

const MANAGE_PLAN_PURCHASE_MODE = 'MANAGE_PLAN';

const planStrings = PLAN_STRINGS_CONFIG;

/*
  Handles:
  1) preselecting plan
  2) getting the list of selectable plans to be displayed with strings/image from PlanStrings.json *
  3) setting purchase mode to manage (change) plan if appropriate


  *For change plans, the user's active subscription may be included in list of "selectable plans"
  even though it's not selectable.  It's necessary since it's displayed on Change Plans Screen.
*/

export const useSelectPlans = (params = {}) => {
  const {
    /* id or name */
    preSelectedPlan = '',
    /* group of string in planstrings.json */
    isPlansCopyV3 = false,
    /* if true, if no matching plan, just select first available plan by default */
    defaultSelect = true,
  } = params;
  const prevIsAuthenticated = useRef(false);

  const dispatch = useDispatch();
  const [selectablePlans, setSelectablePlans] = useState([]);

  const subscription = useSubscription();
  const { isSuccess, hasSubscription, isChangePlanAvailable } = subscription;
  const isAuthenticated = useSelector((s) => s.user.isAuthenticated);
  const { defaultSelectPlans, churnedDefaultSelectPlans } = useSelector((s) => s.appConfig.defaultConfig);
  const plans = useSelector((s) => s.plans);
  const { getPlansSuccess, isFetchingPlans } = plans;

  useEffect(() => {
    const justSignedIn = isAuthenticated && !prevIsAuthenticated.current;
    if ((justSignedIn || !getPlansSuccess) && !isFetchingPlans) {
      console.log('ACTION: FETCH_PACKAGES');
      dispatch(getPlans());
      prevIsAuthenticated.current = true;
    }
  }, [dispatch, getPlansSuccess, isFetchingPlans, isAuthenticated]);

  useEffect(() => {
    const setSelectPlans = async () => {
      //list of (usually) possible allowable plans from FE config
      let defaultPlans = subscription.isChurnedUser ? churnedDefaultSelectPlans : defaultSelectPlans;

      //will want to re-select selectedPlan (if still selectable) after user is signed in
      const planToSelect = plans.selectedPlanId || preSelectedPlan;

      const isChangePlans = subscription.hasActiveSubscription;

      //getting final list of selectable plans
      const availablePlans = isChangePlans
        ? getAvailableChangePlans(plans.allPlans, defaultPlans, subscription, planToSelect)
        : getAvailableSignUpPlans(plans.allPlans, defaultPlans, subscription, planToSelect);

      //addding strings and images from planstrings.json to final list
      let plansList = getPlansWithConfigStrings(availablePlans, planStrings, isPlansCopyV3);

      //finding plan to select
      let { planId = '' } = getPlanFromList(planToSelect, plansList, subscription.activeSubscriptionId);

      //need to have a plan selected by default in signup in default plan flow experiment
      //this select would also apply to deeplinks to payment page for both signup plans and change plans
      if (!planId && defaultSelect && isDefaultPlanFlow()) {
        const defaultPlanIndex = getSetDefaultPlanIndex(plansList.length);
        planId = plansList[defaultPlanIndex] ? plansList[defaultPlanIndex].planId : '';

        //for change plans via payment deeplink, need to make sure not selecting current plans
        if (planId === subscription.activeSubscriptionId) {
          if (plansList[defaultPlanIndex - 1]) {
            planId = plansList[defaultPlanIndex - 1].planId;
          } else {
            planId = plansList[defaultPlanIndex + 1].planId || '';
          }
        }
      }

      if (planId) {
        dispatch(selectPlan(planId));
      } else {
        dispatch(clearSelectedPlan());
      }
      setSelectablePlans(plansList);
    };

    if (plans.allPlans.length !== 0 && (!isAuthenticated || (isAuthenticated && subscription.isStatusSuccess))) {
      setSelectPlans();
    }
  }, [
    plans.allPlans,
    plans.selectedPlanId, //fix later, currently re-runs this useEffect after selects plan
    defaultSelectPlans,
    churnedDefaultSelectPlans,
    isAuthenticated,
    isPlansCopyV3,
    subscription,
    dispatch,
    defaultSelect,
    preSelectedPlan,
  ]);

  // set manage purchaseMode for existing active subscription users.
  useEffect(() => {
    if (plans.getPlansSuccess && isSuccess && hasSubscription && isChangePlanAvailable) {
      dispatch(setPurchaseMode(MANAGE_PLAN_PURCHASE_MODE));
    }
  }, [dispatch, plans, isSuccess, hasSubscription, isChangePlanAvailable]);

  return selectablePlans;
};
